/* ════════════════════════════════════════════════════════════════════════
   📝  WHERE TO EDIT SITE CONTENT  — see /app/docs/CONTENT_EDITING_GUIDE.md
   ────────────────────────────────────────────────────────────────────────
   • SERVICES (below)   → copy shown on /services and /services/:slug
   • INDUSTRIES (below) → copy shown on /industries and /industries/:slug
   • To add an extra free-form section to a service/industry DETAIL page,
     add an `extraSections: [{ heading, body }]` array to that object — it
     renders automatically (see ServiceDetail.jsx / IndustryDetail.jsx).
   • Hero / card images live in SERVICE_HERO_IMAGES, INDUSTRY_HERO_IMAGES
     and PAGE_HERO_IMAGES near the bottom of this file.
   • TEAM member content is NOT here — it is managed in the Admin portal
     (Team tab) and stored in MongoDB (see backend/models.py → TeamMember).
   ════════════════════════════════════════════════════════════════════════ */

export const SERVICES = [
    {
        slug: "business-consulting",
        title: "Business Consulting",
        tagline: "Operational clarity. Strategic momentum.",
        summary:
            "Sharpen operations, decode workflows and unlock measurable efficiency across the entire business stack.",
        problems: [
            "Fragmented operations and unclear ownership",
            "Manual workflows draining productivity",
            "Lack of strategic direction at the operations layer",
        ],
        outcomes: [
            "Documented, optimized process landscape",
            "Reduced operational overhead",
            "Decision-ready operating model",
        ],
        offerings: [
            "Operational optimization",
            "Business process improvement",
            "Strategic advisory",
            "Workflow mapping",
            "Infrastructure enhancement",
        ],
        industries: ["Hospitality", "Retail", "Logistics", "Manufacturing"],
    },
    {
        slug: "analytics-bi",
        title: "Analytics & Business Intelligence",
        tagline: "From raw data to decisive insight.",
        summary:
            "Design and deploy modern analytics ecosystems — dashboards, KPIs and reporting architectures that actually drive action.",
        problems: [
            "Reports scattered across teams and tools",
            "No single source of truth",
            "KPIs that don't reflect business reality",
        ],
        outcomes: [
            "Executive-grade dashboards",
            "Self-service analytics culture",
            "Trustworthy, governed data",
        ],
        offerings: [
            "Dashboard development",
            "KPI systems",
            "Power BI consulting",
            "Reporting architecture",
            "Performance tracking",
        ],
        industries: ["Retail", "Hospitality", "Supply Chain", "Technology"],
    },
    {
        slug: "it-consulting",
        title: "IT Consulting",
        tagline: "Modern systems. Resilient backbones.",
        summary:
            "Architect, modernize and optimize your technology stack to support sustainable, secure growth.",
        problems: [
            "Aging infrastructure slowing the business",
            "High technical debt, low velocity",
            "Misaligned tech investments",
        ],
        outcomes: [
            "Future-proof technology foundation",
            "Lower operating cost",
            "Scalable, secure platforms",
        ],
        offerings: [
            "Infrastructure consulting",
            "Systems optimization",
            "Digital transformation",
            "Technology advisory",
        ],
        industries: ["Technology", "SMEs", "Manufacturing", "Logistics"],
    },
    {
        slug: "process-automation",
        title: "Process Automation",
        tagline: "Engineer the boring out of the business.",
        summary:
            "Replace repetitive manual operations with reliable, monitored, automated systems that scale with you.",
        problems: [
            "Hours lost to manual tracking and reconciliations",
            "Error-prone copy-paste workflows",
            "No visibility into operational health",
        ],
        outcomes: [
            "Automated end-to-end workflows",
            "Real-time operational monitoring",
            "Higher accuracy, faster delivery",
        ],
        offerings: [
            "Workflow automation",
            "Automated business tracking",
            "Operational monitoring",
            "Internal systems",
        ],
        industries: ["Logistics", "Supply Chain", "Retail", "Hospitality"],
    },
    {
        slug: "marketing-brand-growth",
        title: "Marketing & Brand Growth",
        tagline: "Audience, awareness, acquisition.",
        summary:
            "End-to-end marketing programs — strategy, SEO, content, social and paid acquisition — built to turn visibility into measurable revenue.",
        problems: [
            "Inconsistent brand presence across channels",
            "Traffic that doesn't convert into qualified leads",
            "No clear marketing ROI story",
        ],
        outcomes: [
            "Coherent brand and messaging system",
            "Predictable, measurable acquisition funnel",
            "Compounding organic visibility",
        ],
        offerings: [
            "Marketing strategy & planning",
            "SEO & content marketing",
            "Social media management",
            "Paid acquisition (Meta, Google, TikTok)",
            "Brand positioning & messaging",
            "Email & lifecycle marketing",
        ],
        industries: [
            "D2C Brands",
            "SMEs",
            "Marketplace Sellers",
            "Hospitality",
            "Retail",
        ],
    },
    {
        slug: "ecommerce-online-sales",
        title: "E-commerce & Online Sales",
        tagline: "Stores that sell. Storefronts that scale.",
        summary:
            "Set up, optimize and operate online stores and marketplace channels — WooCommerce, Shopify, Wix, plus Amazon, eBay and Etsy — with the operational support to run them day-to-day.",
        problems: [
            "No online presence or an underperforming store",
            "Listings scattered across marketplaces with no consistency",
            "Day-to-day store ops eating the founder's calendar",
        ],
        outcomes: [
            "Conversion-ready online storefront",
            "Multi-channel marketplace presence",
            "Hands-off store operations",
        ],
        offerings: [
            "Shopify, WooCommerce & Wix store builds",
            "Amazon, eBay & Etsy seller setup",
            "Multi-channel product listing & sync",
            "Product photography & copy direction",
            "Order, inventory & customer-service operations",
            "Virtual-assistant support for store owners",
        ],
        industries: [
            "D2C Brands",
            "Marketplace Sellers",
            "SMEs",
            "Retail",
            "Hospitality",
        ],
    },
    {
        slug: "digital-transformation",
        title: "Digital Transformation",
        tagline: "Reimagine how the business runs.",
        summary:
            "A pragmatic, outcome-led approach to modernizing operations, technology and decision-making across the enterprise.",
        problems: [
            "Disjointed digital initiatives",
            "Change without measurable outcomes",
            "Tech-led rather than business-led transformation",
        ],
        outcomes: [
            "Aligned business + technology roadmap",
            "Measurable ROI on every initiative",
            "Modern operating model",
        ],
        offerings: [
            "Transformation roadmap",
            "Operating model design",
            "Change enablement",
            "Tailored business tools",
        ],
        industries: ["Hospitality", "Retail", "Logistics", "Technology", "SMEs"],
    },
];

export const INDUSTRIES = [
    {
        slug: "hospitality",
        title: "Hospitality & Hotels",
        summary:
            "Property-level intelligence, occupancy analytics and guest insights for modern hospitality groups.",
        challenges: [
            "Fragmented PMS and POS data",
            "No unified occupancy & revenue view",
            "Guest insights trapped inside silos",
        ],
        solutions: [
            "Unified operational reporting",
            "Occupancy & revenue intelligence",
            "Customer & guest analytics",
        ],
        useCases: [
            "Group-wide revenue dashboards",
            "Real-time occupancy KPI tracking",
            "F&B operational analytics",
        ],
    },
    {
        slug: "retail",
        title: "Retail & E-commerce",
        summary:
            "Inventory clarity, channel performance and customer insight engines for retail at any scale.",
        challenges: [
            "Inventory mismatches across channels",
            "Lack of customer segmentation",
            "Margin leakage in operations",
        ],
        solutions: [
            "Inventory optimization systems",
            "Multi-channel sales dashboards",
            "Customer & cohort analytics",
        ],
        useCases: [
            "SKU-level performance dashboards",
            "Store vs. online attribution",
            "Customer lifetime value modeling",
        ],
    },
    {
        slug: "logistics",
        title: "Logistics & Supply Chain",
        summary:
            "Movement-level visibility, SLA monitoring and supply chain intelligence for operationally complex businesses.",
        challenges: [
            "No real-time visibility on shipments",
            "Manual SLA & exception tracking",
            "Disconnected vendor & route data",
        ],
        solutions: [
            "Operational visibility platforms",
            "Movement tracking systems",
            "Performance monitoring & alerts",
        ],
        useCases: [
            "Live shipment dashboards",
            "Vendor SLA scorecards",
            "Route performance analytics",
        ],
    },
    {
        slug: "manufacturing",
        title: "Manufacturing",
        summary:
            "Process telemetry, throughput intelligence and operational efficiency for production-driven organizations.",
        challenges: [
            "Plant-floor data not surfaced to leadership",
            "Manual quality & throughput tracking",
            "Reactive maintenance cycles",
        ],
        solutions: [
            "Process monitoring systems",
            "Operational efficiency dashboards",
            "Production KPI architectures",
        ],
        useCases: [
            "OEE dashboards",
            "Throughput vs. plan tracking",
            "Quality issue analytics",
        ],
    },
    {
        slug: "technology",
        title: "IT & Technology Firms",
        summary:
            "Architecture, infrastructure and engineering advisory for product, platform and SaaS companies.",
        challenges: [
            "Scaling architecture under product growth",
            "Fragmented engineering tooling",
            "Unclear platform investment strategy",
        ],
        solutions: [
            "Systems consulting",
            "Software advisory",
            "Infrastructure improvement",
        ],
        useCases: [
            "Architecture reviews",
            "Cloud cost optimization",
            "Platform strategy engagements",
        ],
    },
    {
        slug: "d2c-brands",
        title: "D2C Brands & Online Retail",
        summary:
            "End-to-end support for direct-to-consumer brands selling through their own Shopify, WooCommerce or Wix storefronts.",
        challenges: [
            "No clear acquisition channel mix",
            "Store conversion stuck below industry benchmarks",
            "Founders running ops, marketing and support alone",
        ],
        solutions: [
            "Conversion-optimized store builds",
            "Performance marketing programs",
            "Operations & customer-service support",
        ],
        useCases: [
            "Shopify launch & growth programs",
            "Paid social acquisition funnels",
            "Lifecycle email & retention systems",
        ],
    },
    {
        slug: "marketplace-sellers",
        title: "Marketplace Sellers",
        summary:
            "Setup and operations support for Amazon, eBay and Etsy sellers — from listings to fulfilment to reviews.",
        challenges: [
            "Listings scattered, inconsistent and underperforming",
            "Manual price, inventory and order syncing",
            "Review and reputation management neglected",
        ],
        solutions: [
            "Multi-channel listing & catalog setup",
            "Inventory & order sync workflows",
            "Review, reputation & dispute handling",
        ],
        useCases: [
            "Amazon storefront builds",
            "Etsy shop optimization",
            "eBay listing automation",
        ],
    },
    {
        slug: "smes",
        title: "SMEs & Growing Businesses",
        summary:
            "Pragmatic digital transformation, marketing and operational scaling for ambitious small and medium businesses.",
        challenges: [
            "Manual back-office operations",
            "Founder dependency on operations",
            "No consolidated marketing or reporting",
        ],
        solutions: [
            "Digital transformation programs",
            "Marketing & online presence build-out",
            "Operational scaling playbooks",
        ],
        useCases: [
            "Owner-level KPI dashboards",
            "Local SEO & social presence",
            "Workflow automation for sales & ops",
        ],
    },
];

export const INSIGHTS = [
    {
        slug: "building-modern-operational-dashboards",
        title: "Building Modern Operational Dashboards That Executives Actually Use",
        excerpt:
            "Most dashboards die in pilot. The ones that survive solve a real decision, in a real meeting, every week. Here's the operating model behind dashboards that stick.",
        category: "Business Intelligence",
        readTime: "8 min read",
        date: "Nov 2025",
    },
    {
        slug: "automation-without-chaos",
        title: "Automation Without Chaos: A Framework for Mid-Market Operations",
        excerpt:
            "Process automation fails when teams automate noise. We share the prioritization framework MIR Consulting uses to deliver durable automation in 90 days.",
        category: "Process Automation",
        readTime: "6 min read",
        date: "Oct 2025",
    },
    {
        slug: "kpis-that-drive-decisions",
        title: "KPIs That Drive Decisions, Not Decoration",
        excerpt:
            "A practical guide to designing KPI systems that align with the operating model — and survive the next leadership review.",
        category: "Analytics",
        readTime: "7 min read",
        date: "Oct 2025",
    },
    {
        slug: "digital-transformation-roadmap",
        title: "A Pragmatic Digital Transformation Roadmap for Mid-Market Companies",
        excerpt:
            "Transformation is rarely about technology first. We share the four-phase roadmap MIR Consulting uses with hospitality, retail and logistics clients.",
        category: "Digital Transformation",
        readTime: "9 min read",
        date: "Sep 2025",
    },
];

export const CAPABILITIES = [
    "Business Strategy",
    "Operational Optimization",
    "Power BI & Analytics",
    "Marketing & Brand Growth",
    "E-commerce & Online Sales",
    "Process Automation",
    "Digital Transformation",
    "IT Advisory",
    "Dashboard Development",
];

export const INDUSTRY_TAGS = [
    "Hospitality",
    "Retail",
    "Logistics",
    "Supply Chain",
    "Manufacturing",
    "Technology",
    "D2C Brands",
    "Marketplace Sellers",
    "SMEs",
];

// --- Hero imagery (slug → image URL). Pages render a premium gradient/grid
// backdrop underneath, so a 404 on any of these degrades gracefully. ---
export const SERVICE_HERO_IMAGES = {
    "business-consulting":
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1600",
    "analytics-bi":
        "https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "it-consulting":
        "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1600",
    "process-automation":
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1600",
    "marketing-brand-growth":
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1600",
    "ecommerce-online-sales":
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=1600",
    "digital-transformation":
        "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1600",
};

export const INDUSTRY_HERO_IMAGES = {
    hospitality:
        "https://images.unsplash.com/photo-1551918120-9739cb430c6d?auto=format&fit=crop&q=80&w=1600",
    retail:
        "https://images.unsplash.com/photo-1481437156560-3205f6a55735?auto=format&fit=crop&q=80&w=1600",
    logistics:
        "https://images.unsplash.com/photo-1557761469-f29c6e201784?auto=format&fit=crop&q=80&w=1600",
    manufacturing:
        "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&q=80&w=1600",
    technology:
        "https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "d2c-brands":
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=1600",
    "marketplace-sellers":
        "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&q=80&w=1600",
    smes:
        "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1600",
};

export const PAGE_HERO_IMAGES = {
    about:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1600",
    ourWork:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1600",
    services:
        "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&q=80&w=1600",
    industries:
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1600",
    contact:
        "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80&w=1600",
    blog:
        "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=1600",
    caseStudies:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1600",
};

// Selector options for admin content tagging (Phase D)
export const SERVICE_OPTIONS = SERVICES.map((s) => ({ slug: s.slug, title: s.title }));
export const INDUSTRY_OPTIONS = INDUSTRIES.map((i) => ({ slug: i.slug, title: i.title }));
